import React, { useEffect, useRef, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import api from '../services/api';
import toast from 'react-hot-toast';
import useTurnstileChallenge from '../lib/useTurnstileChallenge';
import { OTP_TEXT, otpChannelLabel, otpChannelOf, otpSentText, responseAdvertisedOtp, useOtpDelivery, useSecondsUntil } from '../lib/otpChannel';

// Cloudflare-Turnstile-Site-Key (öffentlich). Ist er gesetzt, erscheint die Bot-Challenge.
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// VOOGO-Wortmarke der Registrierungsschritte (einmal definiert: Typo-Wächter zählt die Wortmarke).
function RegisterBrand({ subtitle }) {
  return (
    <div className="pp-auth-brand" style={{ textAlign: 'center', marginBottom: 28 }}>
      <h1 style={{ fontSize: 40, fontWeight: 900, letterSpacing: -2 }}>V<span style={{ color: '#9C27B0' }}>OO</span>GO</h1>
      <p style={{ color: '#4b5563', fontSize: 14 }}>{subtitle}</p>
    </div>
  );
}

// Bestehende Wartezeit des Servers für einen neuen Code (backend lib/sms.js RESEND_COOLDOWN_MS).
const PHONE_RESEND_WAIT_MS = 60 * 1000;

export default function Register({ setup }) {
  const [step, setStep] = useState(1);
  const [userForm, setUserForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [companyForm, setCompanyForm] = useState({ company_name: '', company_address: '', tax_id: '' });
  const [ageAgb, setAgeAgb] = useState(false);   // Volljährigkeit (18+) + AGB/Datenschutz (Server erzwingt es für das Nutzer-Konto)
  const [dsgvo, setDsgvo] = useState(false);
  const [loading, setLoading] = useState(false);
  const { acceptPartnerRegistration, logout, pendingPhoneVerification, finishPhoneVerification, isCurrentSession, restoreSession } = useAuth();
  const activeRef = useRef(false);
  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; };
  }, []);
  const navigate = useNavigate();

  const [unknownAccount, setUnknownAccount] = useState(false);
  const [unknownCompany, setUnknownCompany] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const { challenge, challengeView, widgetContainerRef } = useTurnstileChallenge({
    enabled: !setup && step === 1 && !accountCreated,
    siteKey: SITE_KEY,
    action: 'partner_register',
  });

  const registerUser = async (e) => {
    e.preventDefault();
    if (accountCreated || unknownAccount) return;
    if (!ageAgb) { toast.error('Bitte bestätige Volljährigkeit und akzeptiere die AGB.'); return; }
    const attempt = challenge.beginSubmit();
    if (!attempt) { toast.error('Bitte die Bot-Prüfung abschließen.'); return; }
    let created = false;
    const current = () => activeRef.current && isCurrentSession();
    try {
      const response = await api.post('/auth/register', {
        ...userForm, role: 'partner', turnstile_token: attempt.token,
        age_confirmed: true, agb_accepted: true,
      }, { timeout: 15000 });
      if (!current()) return;
      created = true;
      setAccountCreated(true);
      // WHATSAPP-OTP-STANDARD-001: Hat der Server einen Telefoncode verschickt (phone_verify_required),
      // folgt nach der Anmeldung zuerst der Code-Schritt. `channel` nur, wenn die Anfrage das Token trug.
      const data = response?.data;
      const phoneVerification = data?.phone_verification_required === true ? {
        channel: otpChannelOf(data, responseAdvertisedOtp(response)),
        target: typeof data.user?.masked_phone === 'string' ? data.user.masked_phone : (typeof data.phone === 'string' ? data.phone : null),
        resendAt: data.verification?.phone_code_sent === false ? 0 : Date.now() + PHONE_RESEND_WAIT_MS,
        restored: data.verification?.phone_code_sent === false,
        phoneMissing: data.user?.phone_missing === true,
      } : null;
      if (await acceptPartnerRegistration(response.data, { phoneVerification }) === false) return;
      challenge.loginSucceeded();
      setStep(2);
    } catch (err) {
      if (!current()) return;
      if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
        setUnknownAccount(true);
        challenge.loginFailed();
        return;
      }
      if (created) {
        challenge.loginSucceeded();
        toast.error('Dein Konto wurde angelegt. Bitte melde dich an, um die Firmendaten einzugeben.');
      } else {
        challenge.loginFailed(err.response?.status);
        const data = err.response?.data;
        const fields = data?.fields;
        const detail = fields && typeof fields === 'object'
          ? Object.values(fields).filter(value => typeof value === 'string').join(' ') : '';
        toast.error(data?.error === 'turnstile_failed'
          ? 'Bitte die Bot-Prüfung erneut bestätigen und noch einmal versuchen.'
          : (detail || data?.message || data?.error || 'Registrierung fehlgeschlagen. Bitte erneut versuchen.'));
      }
    }
  };

  const registerCompany = async (e) => {
    e.preventDefault();
    if (unknownCompany) return;
    if (companyForm.company_address.trim().length < 6) return toast.error('Bitte vollständige Firmenanschrift angeben (Straße, PLZ, Ort)');
    if (companyForm.tax_id.trim().length < 5) return toast.error('Bitte Steuernummer oder USt-IdNr. angeben');
    if (!dsgvo) return toast.error('Bitte Datenschutzerklärung akzeptieren');
    setLoading(true);
    const current = () => activeRef.current && isCurrentSession();
    try {
      const response = await api.post('/partners/register', companyForm, { timeout: 15000 });
      if (!current()) return;
      toast.success(response.data?.message || 'Partnerprofil angelegt. Bitte melde dich jetzt als Partner an.');
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      if (!current()) return;
      if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
        setUnknownCompany(true);
        toast.error('Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.');
      } else toast.error(err.response?.data?.error || 'Fehler');
    } finally { if (current()) setLoading(false); }
  };

  const inp = { width: '100%', padding: '11px 13px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: '#111', marginBottom: 14 };

  if (setup && pendingPhoneVerification) {
    return <PhoneCodeStep pending={pendingPhoneVerification} onDone={finishPhoneVerification} onLogout={logout} />;
  }

  if (setup || step === 2) return (
    <div className="pp-auth-shell is-register" style={{ minHeight: '100vh', background: '#f4f5f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="pp-auth-column" style={{ width: '100%', maxWidth: 520 }}>
        <RegisterBrand subtitle="Firmendaten eingeben" />
        <div className="pp-auth-card" style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <div className="pp-auth-steps" style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {['Nutzer', 'Firma', 'Dokumente'].map((s, i) => (
              <div key={s} style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: 6, background: i === 1 ? '#000' : '#f4f5f6', color: i === 1 ? '#fff' : '#6b7280', fontSize: 12, fontWeight: 700 }}>{s}</div>
            ))}
          </div>
          {unknownCompany && <p role="status">Keine Antwort bestätigt. Prüfe zuerst den Firmenstatus. <button type="button" onClick={restoreSession}>Firmenstatus prüfen</button></p>}
          <form onSubmit={registerCompany}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5 }}>Firmenname *</label>
            <input value={companyForm.company_name} onChange={e => setCompanyForm({...companyForm, company_name: e.target.value})} placeholder="Mustermann Transporte GmbH" required style={inp} onFocus={e => e.target.style.borderColor='#000'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />

            <label style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5 }}>Firmenanschrift * <span style={{ textTransform: 'none', color: '#6b7280', fontWeight: 500 }}>(Straße, PLZ, Ort)</span></label>
            <input value={companyForm.company_address} onChange={e => setCompanyForm({...companyForm, company_address: e.target.value})} placeholder="Musterstraße 1, 10115 Berlin" required style={inp} onFocus={e => e.target.style.borderColor='#000'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />

            <label style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5 }}>Steuernummer / USt-IdNr. *</label>
            <input value={companyForm.tax_id} onChange={e => setCompanyForm({...companyForm, tax_id: e.target.value})} placeholder="DE123456789" required style={inp} onFocus={e => e.target.style.borderColor='#000'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
            <p style={{ fontSize: 12, color: '#6b7280', margin: '-8px 0 14px', lineHeight: 1.5 }}>Pflichtangaben für rechtskonforme Rechnungen (§ 14 UStG).</p>

            <div style={{ padding: 14, background: '#f3f4f6', borderRadius: 10, marginBottom: 16, border: '1px solid #d1d5db' }}>
              <p style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.5 }}>
                <ClipboardList size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> <strong>Nach der Registrierung</strong> kannst du im Portal deine Dokumente hochladen (Gewerbeanmeldung, Konzession pro Fahrzeug). Der Admin prüft und gibt dein Konto frei.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              <input type="checkbox" id="dsgvo" checked={dsgvo} onChange={e => setDsgvo(e.target.checked)} style={{ marginTop: 3, accentColor: '#000' }} />
              <label htmlFor="dsgvo" style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.5, cursor: 'pointer' }}>
                Ich habe die <strong style={{ color: '#000' }}>Datenschutzerklärung (DSGVO)</strong> zur Partnerregistrierung gelesen.
              </label>
            </div>

            <button type="submit" disabled={loading || unknownCompany}
              style={{ width: '100%', padding: '12px', background: loading ? '#e5e7eb' : '#000', color: loading ? '#6b7280' : '#fff', borderRadius: 8, fontSize: 14, fontWeight: 800 }}>
              {loading ? 'Wird eingereicht...' : 'Registrierung einreichen'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pp-auth-shell is-register" style={{ minHeight: '100vh', background: '#f4f5f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="pp-auth-column" style={{ width: '100%', maxWidth: 520 }}>
        <RegisterBrand subtitle="Als Mietwagenunternehmer registrieren" />
        <div className="pp-auth-card" style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <div className="pp-auth-steps" style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {['Nutzer', 'Firma', 'Dokumente'].map((s, i) => (
              <div key={s} style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: 6, background: i === 0 ? '#000' : '#f4f5f6', color: i === 0 ? '#fff' : '#6b7280', fontSize: 12, fontWeight: 700 }}>{s}</div>
            ))}
          </div>
          <form onSubmit={registerUser}>
            {[
              { key: 'name', label: 'Vollständiger Name *', type: 'text', ph: 'Max Mustermann' },
              { key: 'email', label: 'E-Mail *', type: 'email', ph: 'max@firma.de' },
              { key: 'phone', label: 'Telefon *', type: 'tel', ph: '+49 170 1234567' },
              { key: 'password', label: 'Passwort *', type: 'password', ph: '••••••••' },
            ].map(({ key, label, type, ph }) => (
              <div key={key}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>{label}</label>
                <input type={type} value={userForm[key]} onChange={e => setUserForm({ ...userForm, [key]: e.target.value })} placeholder={ph} required={['name','email','password'].includes(key)} minLength={key === 'password' ? 8 : undefined}
                  style={inp} onFocus={e => e.target.style.borderColor='#000'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, margin: '2px 0 14px' }}>
              <input type="checkbox" id="ageAgb" checked={ageAgb} onChange={e => setAgeAgb(e.target.checked)} style={{ marginTop: 3, accentColor: '#000' }} />
              <label htmlFor="ageAgb" style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.5, cursor: 'pointer' }}>
                Ich bin mindestens 18 Jahre alt und akzeptiere die <strong style={{ color: '#000' }}>AGB</strong> und <strong style={{ color: '#000' }}>Datenschutzerklärung</strong>.
              </label>
            </div>
            {!accountCreated && !unknownAccount && (SITE_KEY ? <div ref={widgetContainerRef} style={{ margin: '4px 0 14px' }} /> : (
              <p role="alert">Bot-Prüfung ist derzeit nicht verfügbar. Bitte später erneut versuchen.</p>
            ))}
            {!accountCreated && !unknownAccount && challengeView.retryReason && <p role="status">
              Bitte bestätige die Bot-Prüfung erneut. Deine Eingaben bleiben erhalten.{' '}
              {!challengeView.submitting && <button type="button" onClick={() => challenge.retryWidget()}>Neu laden</button>}
            </p>}
            {unknownAccount && <p role="status">Keine Antwort bestätigt. Dein Konto kann bereits angelegt sein. <Link to="/login">Anmelden und Kontostand prüfen</Link></p>}
            {accountCreated && <p role="status">Dein Konto wurde angelegt. <Link to="/login">Anmelden und Firmendaten ergänzen</Link></p>}
            <button type="submit" disabled={!challengeView.canSubmit || accountCreated || unknownAccount}
              style={{ width: '100%', padding: '12px', background: challengeView.canSubmit ? '#000' : '#e5e7eb', color: challengeView.canSubmit ? '#fff' : '#6b7280', borderRadius: 8, fontSize: 14, fontWeight: 800 }}>
              {challengeView.submitting ? '...' : 'Weiter →'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: 14, color: '#6b7280', fontSize: 13 }}>
            Bereits registriert? <Link to="/login" style={{ color: '#000', fontWeight: 700 }}>Anmelden</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// WHATSAPP-OTP-STANDARD-001 — Telefoncode nach der Registrierung bestätigen.
// Endpunkte: /auth/me/phone/verify/confirm + /request (REST-Pfad aus STORED-PHONE-VERTRAG-001 mit
// Fehlerkennungen `error` + Satz `message`). Gleicher Code-Bereich wie der Registrierungscode
// (ownPhoneVerificationOtpScope + PHONE_VERIFICATION_SMS), also ist genau dieser Code einlösbar und
// „Lieber per SMS" findet den WhatsApp-Versand. /verify-phone/* bleibt der eingefrorene Altvertrag.
function PhoneCodeStep({ pending, onDone, onLogout }) {
  const { isCurrentSession } = useAuth();
  const activeRef = useRef(false);
  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; };
  }, []);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingPhone, setEditingPhone] = useState(pending.phoneMissing === true);
  const [newPhone, setNewPhone] = useState('');
  const [requestedPhone, setRequestedPhone] = useState('');
  const [changingPhone, setChangingPhone] = useState(false);
  const [target, setTarget] = useState(pending.target);
  const [needsNewCode, setNeedsNewCode] = useState(false);
  const [resendAt, setResendAt] = useState(pending.resendAt || 0);
  const otp = useOtpDelivery();
  const { receive } = otp;
  useEffect(() => { receive(pending.channel || null); }, [pending, receive]);
  const resendIn = useSecondsUntil(resendAt);
  const busyRef = useRef(false);   // Doppelklick-Schutz über alle Knöpfe des Schritts

  const run = async (task) => {
    if (busyRef.current) return false;
    busyRef.current = true; setBusy(true);
    const current = () => activeRef.current && isCurrentSession();
    try { return await task(current); } finally {
      if (current()) { busyRef.current = false; setBusy(false); }
    }
  };

  const confirm = (e) => {
    e.preventDefault();
    const current = () => activeRef.current && isCurrentSession();
    run(async () => {
      try {
        await api.post(changingPhone ? '/auth/me/phone/confirm' : '/auth/me/phone/verify/confirm', { code: code.trim() }, { timeout: 15000 });
        if (!current() || onDone() === false) return;
        toast.success('Telefonnummer bestätigt.');
      } catch (err) {
        if (!current()) return;
        if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') { toast.error('Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.'); return; }
        setCode('');
        if (err.response?.data?.next === 'request_code') {
          setNeedsNewCode(true); setResendAt(0); otp.reset();
        }
        toast.error(err.response?.data?.message || 'Code ungültig oder abgelaufen. Bitte fordere einen neuen Code an.');
      }
    });
  };

  const requestCode = (preferSms) => run(async (current) => {
    try {
      const response = await api.post(changingPhone ? '/auth/me/phone/request' : '/auth/me/phone/verify/request',
        { ...(changingPhone ? { phone: requestedPhone } : {}), ...(preferSms ? { channel: 'sms' } : {}) }, { timeout: 15000 });
      if (!current()) return false;
      const data = response?.data;
      if (data?.already_verified === true || data?.already === true) {
        if (onDone() === false) return false;
        toast.success('Telefonnummer ist bereits bestätigt.');
        return true;
      }
      if (data?.sent !== true) { toast.error('Der Code konnte nicht zugestellt werden.'); return false; }
      const channel = otpChannelOf(data, responseAdvertisedOtp(response));
      receive(channel); setCode(''); setNeedsNewCode(false);
      if (!preferSms) setResendAt(Date.now() + PHONE_RESEND_WAIT_MS);
      toast.success(`Code per ${otpChannelLabel(channel)} gesendet.`);
      return true;
    } catch (err) {
      if (!current()) return false;
      if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') { toast.error('Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.'); return false; }
      const data = err.response?.data;
      if (data?.error === 'resend_too_soon') {
        const wait = Number(data.retry_after_seconds) || 60;
        setResendAt(Date.now() + wait * 1000);
        toast.error(`Bitte warte noch ${wait} Sekunden, bevor du einen neuen Code anforderst.`);
      } else {
        toast.error(data?.message || 'Der Code konnte nicht zugestellt werden.');
      }
      return false;
    }
  });
  const requestChangedPhone = (e) => {
    e.preventDefault();
    run(async (current) => {
      try {
        const response = await api.post('/auth/me/phone/request', { phone: newPhone }, { timeout: 15000 });
        if (!current()) return;
        if (response.data?.sent !== true) throw new Error('delivery');
        setTarget(response.data.phone || null); setRequestedPhone(newPhone); setChangingPhone(true); setEditingPhone(false);
        setCode(''); setNeedsNewCode(false); setResendAt(Date.now() + PHONE_RESEND_WAIT_MS);
        receive(otpChannelOf(response.data, responseAdvertisedOtp(response)));
        toast.success('Code an die neue Telefonnummer gesendet.');
      } catch (err) {
        if (!current()) return;
        toast.error(err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT' ? 'Keine Antwort bestätigt. Prüfe erhaltene Codes oder deinen aktuellen Kontostand, bevor du bewusst erneut versuchst.' : (err.response?.data?.message || 'Der Code konnte nicht zugestellt werden. Bitte erneut versuchen.'));
      }
    });
  };
  const preferSms = async () => {
    if (busyRef.current || !otp.beginSmsSwitch()) return;
    if (!(await requestCode(true))) otp.failSmsSwitch();
  };

  // Gesperrt voll deckend (#6b7280): die globale Regel button:disabled { opacity:.5 } wäre unter 4,5:1.
  const link = (enabled) => ({ background: 'none', border: 'none', padding: 0, color: enabled ? '#4b5563' : '#6b7280', opacity: 1, fontSize: 13, cursor: enabled ? 'pointer' : 'not-allowed', textDecoration: enabled ? 'underline' : 'none' });
  const canSubmit = code.length === 6 && !busy;
  return (
    <div className="pp-auth-shell is-register" style={{ minHeight: '100vh', background: '#f4f5f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="pp-auth-column" style={{ width: '100%', maxWidth: 520 }}>
        <RegisterBrand subtitle="Telefonnummer bestätigen" />
        <div className="pp-auth-card" style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          {editingPhone ? <form onSubmit={requestChangedPhone}>
            <p>Gib die Telefonnummer ein, die du verwenden möchtest. Erst ihr Code bestätigt die Änderung.</p>
            <label htmlFor="required-phone-change">Neue Telefonnummer</label>
            <input id="required-phone-change" type="tel" value={newPhone} onChange={e => setNewPhone(e.target.value)} required autoComplete="tel" />
            <button type="submit" disabled={busy || !newPhone.trim()}>Code senden</button>
            {(!pending.phoneMissing || changingPhone) && <button type="button" disabled={busy} onClick={() => setEditingPhone(false)}>Zurück zum Code</button>}
          </form> : <form onSubmit={confirm}>
            <p data-testid="phone-code-hint" style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, margin: '0 0 14px' }}>
              {needsNewCode ? 'Der bisherige Code ist abgelaufen. Fordere einen neuen Code an.' : otpSentText(otp.channel, pending.restored
                ? 'Bitte bestätige deine Telefonnummer mit dem Code, den wir dir geschickt haben. Kein Code da? Fordere einen neuen an.'
                : 'Wir haben dir einen Code per SMS geschickt.')}
              {target && <><br /><span style={{ color: '#111', fontWeight: 700 }}>an {target}</span></>}
            </p>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>Code</label>
            <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus
              inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456"
              style={{ width: '100%', padding: '11px 13px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 16, color: '#111', marginBottom: 14, letterSpacing: 6, textAlign: 'center' }} />
            <button type="submit" disabled={!canSubmit}
              style={{ width: '100%', padding: '12px', background: canSubmit ? '#000' : '#e5e7eb', color: canSubmit ? '#fff' : '#6b7280', borderRadius: 8, fontSize: 14, fontWeight: 800, border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
              {busy ? 'Wird geprüft …' : 'Bestätigen'}
            </button>
          </form>}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: 14 }}>
            {!editingPhone && <button type="button" disabled={busy} style={link(!busy)} onClick={() => { setEditingPhone(true); setCode(''); }}>Telefonnummer ändern</button>}
            {!editingPhone && <>
            <button type="button" onClick={() => requestCode(false)} disabled={busy || resendIn > 0} style={link(!busy && resendIn === 0)}>
              {resendIn > 0 ? `Code erneut senden (in ${resendIn} s)` : 'Code erneut senden'}
            </button>
            {otp.showsPreferSms && (
              <button type="button" onClick={preferSms} disabled={busy || otp.switching} title={OTP_TEXT.preferSmsHint} style={link(!busy && !otp.switching)}>
                {OTP_TEXT.preferSms}
              </button>
            )}
            </>}
            {/* Owner 05.10.2026: kein „Später“ — ohne bestätigte Nummer geht es nicht zu den Firmendaten. */}
            <button type="button" onClick={onLogout} disabled={busy} style={link(!busy)}>Abmelden</button>
          </div>
        </div>
      </div>
    </div>
  );
}
